/**
 * SetMuteConversationUseCase
 *
 * Applies the requested mute/un-mute state for a conversation with `peerId`
 * following Flutter's optimistic-write-then-rollback orchestration:
 *   1. Optimistic local write FIRST (for instant UI — Flutter does the local
 *      writes "for UI reasons" before the server hop, so the user can reopen
 *      chat actions before the server confirms).
 *   2. Call the server hop via `chatService.setMuteUser`.
 *   3. Roll the local write back (the exact inverse) if the server fails.
 *
 * Unlike a toggle, the `mute` flag is supplied by the caller; the use case
 * simply applies the requested state (parity with the repository: the sheet
 * derives `willMute = !_conversationMuted` and the repository just applies it).
 *
 * Flutter source: lib/data/repository/chat_repository.dart:46-56
 *   (`setMuteUser` — local writes → server → rollback on `!success`) and
 *   :97-108 (`_setHiveMuteUser` — `put` on mute, `delete` on un-mute).
 *
 * Storage key: the web keeps conversation-mute state under the
 * `SECURE_STORAGE_KEYS.MUTED_USERS + peerId` key — the exact key
 * `IsConversationMutedUseCase` reads (isConversationMutedUseCase.ts:36). The
 * optimistic local write MUST target `MUTED_USERS` so the write and the
 * predicate read stay consistent (a re-open of the sheet reflects the new
 * state).
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { chatService } from '@/domain/services/chat/chatService';
import type { MutedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class SetMuteConversationUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Apply the requested mute state for the conversation with `peerId`.
   *
   * @param peerId - The peer user's Firebase id.
   * @param mute - `true` to mute, `false` to un-mute. Supplied by the caller
   *   (no toggling happens here).
   * @returns `true` on success, `false` if the server write fails or anything
   *   throws (the local write is rolled back to its pre-call state on failure).
   */
  async execute(peerId: string, mute: boolean): Promise<boolean> {
    const storageKey = SECURE_STORAGE_KEYS.MUTED_USERS + peerId;

    try {
      // Step 1: Optimistic local write FIRST (for instant UI feedback —
      // parity with chat_repository.dart:47). `mute` -> write the record,
      // `!mute` -> delete it (chat_repository.dart:102-107).
      if (mute) {
        const record: MutedUserStorage = { firebaseId: peerId };
        await this.storage.setEncrypted(storageKey, record);
      } else {
        await this.storage.remove(storageKey);
      }

      // Flutter also runs `setLocalUserMutedUsers(firebaseId, mute)` here, in
      // the same first-local-writes batch (chat_repository.dart:48). It mutates
      // the in-memory `CurrentUserNotifier.user.mutedUsers` list.
      //
      // TODO: @claude add a follow up task for this: setLocalUserMutedUsers in-memory CurrentUser.mutedUsers update — Flutter lib/data/repository/chat_repository.dart:48,58-78
      //
      // Deferral rationale: the in-memory `mutedUsers` list's only consumers in
      // Flutter are `local_notification_service.dart:144-151` (web has no
      // foreground local-notification service) and the sync seed (web re-derives
      // muted state from the server via `syncSecureStorageUseCase.syncMutedUsers`,
      // not the in-memory list). So mirroring this into the Redux
      // `CURRENT_USER.mutedUsers` cache here would be dead code today. The
      // durable mute state the web actually reads is the `MUTED_USERS`
      // SecureStorage record written above.

      // Step 2: Server hop.
      const success = await chatService.setMuteUser(peerId, mute);

      // Step 3: Roll the local write back on failure — the exact inverse of the
      // optimistic write, matching Flutter's `_setHiveMuteUser(firebaseId, !mute)`
      // (chat_repository.dart:51).
      if (!success) {
        if (mute) {
          await this.storage.remove(storageKey);
        } else {
          const record: MutedUserStorage = { firebaseId: peerId };
          await this.storage.setEncrypted(storageKey, record);
        }
        return false;
      }

      return true;
    } catch (error) {
      appLogger.error('Error setting mute conversation', 'SetMuteConversationUseCase.execute', {
        error: error as Error,
      });
      return false;
    }
  }
}

export const setMuteConversationUseCase = new SetMuteConversationUseCase();
